// DD-3735: frontend errors go to our self-hosted GlitchTip (Sentry protocol). Only production
// builds with REACT_APP_GLITCHTIP_DSN report; everywhere else (local, staging, deploy previews) the
// SDK stays off and every function here is a no-op. What gets sent is decided in event-filter.ts.
import * as Sentry from '@sentry/react';
import type { ErrorInfo } from 'react';
import { createBeforeSend } from './event-filter';
import { handleChunkLoadError } from './chunk-reload';

const DSN = process.env.REACT_APP_GLITCHTIP_DSN || '';
const ENVIRONMENT = process.env.REACT_APP_DEPLOY_ENV || 'local';
// Netlify's COMMIT_REF, compiled in by scripts/build-with-sourcemaps.sh (the same id DD-3742 uses
// for version.json). Source maps are matched by debug id, not by release.
const RELEASE = process.env.REACT_APP_BUILD_ID || undefined;

export const isErrorReportingEnabled = (): boolean => !!DSN && ENVIRONMENT === 'production';

export const initErrorReporting = (): void => {
    // Chunk errors after a release reload the tab whether or not reporting is on.
    window.addEventListener('error', (event) => handleChunkLoadError(event.error));
    window.addEventListener('unhandledrejection', (event) => handleChunkLoadError(event.reason));

    if (!isErrorReportingEnabled()) return;

    Sentry.init({
        dsn: DSN,
        environment: ENVIRONMENT,
        release: RELEASE,
        enabled: true,
        sendDefaultPii: false,
        // Error capture only: no tracing, replay or profiling, and only the integrations it needs.
        defaultIntegrations: false,
        integrations: [
            Sentry.eventFiltersIntegration(),
            Sentry.functionToStringIntegration(),
            Sentry.browserApiErrorsIntegration(),
            Sentry.globalHandlersIntegration({ onerror: true, onunhandledrejection: true }),
            Sentry.linkedErrorsIntegration(),
            Sentry.dedupeIntegration(),
            Sentry.httpContextIntegration(),
            // Console output can hold anything, so it is left out. DOM breadcrumbs record the
            // element clicked or typed in, never the value.
            Sentry.breadcrumbsIntegration({ console: false, dom: true, fetch: true, xhr: true, history: true }),
        ],
        // Only errors thrown from our own bundle; event-filter.ts double-checks every frame.
        allowUrls: [new RegExp(`^${window.location.origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/static/js/`)],
        denyUrls: [/^(chrome|moz|safari(-web)?|ms-browser)-extension:\/\//],
        maxBreadcrumbs: 50,
        beforeSend: createBeforeSend(),
    });
};

export type ErrorReportingUser = {
    _id: string;
    fullName?: string;
    email?: string;
    impersonatedBy?: string;
    organization?: { _id?: string; name?: string; type?: string };
};

/**
 * Records who hit an error. DD-3767: name, e-mail address and organization go along with the ids,
 * so errors can be followed per organization in GlitchTip and Grafana (denthub-errors dashboard).
 * `impersonated` marks a super admin acting as the user, so their errors can be told apart.
 */
export const setErrorReportingUser = (user?: ErrorReportingUser): void => {
    if (!isErrorReportingEnabled()) return;
    Sentry.setUser(user ? { id: user._id, email: user.email, username: user.fullName } : null);
    Sentry.setTag('org_id', user?.organization?._id || undefined);
    Sentry.setTag('org_name', user?.organization?.name || undefined);
    Sentry.setTag('org_type', user?.organization?.type || undefined);
    Sentry.setTag('impersonated', user ? String(!!user.impersonatedBy) : undefined);
};

/** For the app's error boundary: the error behind the error screen. */
let lastBoundaryEventId: string | undefined;

/** DD-3780: the GlitchTip event of the last red-screen crash, so a support ticket can point at it. */
export const getLastBoundaryEventId = (): string | undefined => lastBoundaryEventId;

export const reportBoundaryError = (error: unknown, info?: ErrorInfo): void => {
    if (handleChunkLoadError(error) || !isErrorReportingEnabled()) return;
    lastBoundaryEventId = Sentry.captureException(error, {
        tags: { source: 'error-boundary' },
        contexts: info?.componentStack ? { react: { componentStack: info.componentStack } } : undefined,
    });
};

type StatusError = { statusCode?: number };

// Session ends (401) and gateway outages are expected; the user is logged out or told to retry.
const EXPECTED_STATUS_CODES = [401, 502, 503, 504];

/** For the Apollo error link: network errors only, GraphQL errors are logged by the backend. */
export const reportApolloNetworkError = (networkError: Error, operationName?: string): void => {
    if (!isErrorReportingEnabled()) return;
    const statusCode = (networkError as StatusError).statusCode;
    if (statusCode && EXPECTED_STATUS_CODES.includes(statusCode)) return;
    Sentry.captureException(networkError, {
        // Never the variables: they hold patient and order data.
        tags: { source: 'apollo-network', operation_name: operationName || 'unknown', status_code: statusCode },
    });
};

export type UploadFailureReport = {
    /** Which upload, e.g. 'order-files'. Never the URL: it carries the order id. */
    upload: string;
    kind: string;
    fileCount: number;
    totalBytes: number;
    sentBytes: number;
    elapsedMs: number;
};

/**
 * DD-3793: an upload that never got an answer. Sent with its size, how much of it the browser
 * sent and how long it ran, which tells a server timeout (cut at a fixed duration) from a
 * dropped connection. One issue per upload and kind, whatever the file names.
 */
export const reportUploadFailure = (error: unknown, report: UploadFailureReport): void => {
    if (!isErrorReportingEnabled()) return;
    const statusCode = (error as { response?: { status?: number } })?.response?.status;
    Sentry.captureException(error, {
        tags: { source: 'upload', upload: report.upload, upload_failure: report.kind, status_code: statusCode },
        contexts: {
            upload: {
                file_count: report.fileCount,
                total_bytes: report.totalBytes,
                sent_bytes: report.sentBytes,
                elapsed_seconds: Math.round(report.elapsedMs / 1000),
            },
        },
        fingerprint: ['upload-failure', report.upload, report.kind],
    });
};
